/******************************************************
 * From the Vault: image and video carousel
 ******************************************************/

if (!customElements.get("vault-media-carousel")) {
  // Rounding slack, in pixels, when comparing scroll offsets.
  const SCROLL_TOLERANCE = 2;

  // Used when `data-speed` is missing or unreadable.
  const DEFAULT_AUTOPLAY_SPEED = 3;

  const PAUSED_CLASS = "article-vault-from-vault__autoplay--paused";

  const VIDEO_SELECTOR = ".js-vault-carousel-video";

  customElements.define(
    "vault-media-carousel",
    /**
     * Extends the theme's SliderComponent to loop the prev/next arrows, to
     * auto-rotate the slides, and to play video slides while they are current.
     */
    class VaultMediaCarousel extends SliderComponent {
      constructor() {
        super();

        if (!this.slider) return;

        this.videos = Array.from(this.querySelectorAll(VIDEO_SELECTOR));

        // A single slide renders no arrows, and the parent sets nothing up
        // without them -- but a lone video still has to play.
        this.hasArrows = Boolean(this.prevButton && this.nextButton);

        if (this.hasArrows) {
          // The parent fires this only when the slide actually changes, which
          // is far less often than its scroll handler runs.
          this.addEventListener("slideChanged", () => this.onSlideChanged());
          this.setSlideVisibility();
        }

        this.autoplayEnabled =
          this.hasArrows && this.slider.dataset.autoplay === "true";

        // Rotation and video playback are both motion: they share one
        // play/pause control and one set of pause conditions.
        this.motionEnabled = this.autoplayEnabled || this.videos.length > 0;
        if (!this.motionEnabled) return;

        this.autoplaySpeed =
          (parseFloat(this.slider.dataset.speed) || DEFAULT_AUTOPLAY_SPEED) *
          1000;

        /**
         * What the visitor has asked for with the play/pause control:
         * "auto"    -- nobody has expressed a preference (the initial state)
         * "playing" -- they pressed play, which outranks a courtesy pause
         * "stopped" -- they pressed pause
         */
        this.autoplayIntent = "auto";

        // Set once the visitor takes over with an arrow. It stops rotation
        // only: a video they navigate to should still play.
        this.rotationHeld = false;

        this.isHovered = false;
        this.isFocused = false;
        this.isVisible = false;

        this.reducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        );

        this.syncAutoplay = this.syncAutoplay.bind(this);
        this.onReducedMotionChange = this.onReducedMotionChange.bind(this);

        this.autoplayButton = this.querySelector(".js-vault-carousel-autoplay");
        this.autoplayButton?.addEventListener("click", () =>
          this.onAutoplayToggle()
        );

        this.addEventListener("mouseenter", () => {
          this.isHovered = true;
          this.syncAutoplay();
        });
        this.addEventListener("mouseleave", () => {
          this.isHovered = false;
          this.syncAutoplay();
        });
        this.addEventListener("focusin", () => {
          this.isFocused = true;
          this.syncAutoplay();
        });
        this.addEventListener("focusout", () => {
          this.isFocused = false;
          this.syncAutoplay();
        });

        this.reducedMotion.addEventListener("change", this.onReducedMotionChange);
        document.addEventListener("visibilitychange", this.syncAutoplay);

        // Only move while the carousel is on screen, so a visitor further
        // down the page does not come back to a carousel many slides along,
        // and off-screen video does not download or play.
        this.autoplayObserver = new IntersectionObserver((entries) => {
          this.isVisible = entries[0].isIntersecting;
          this.syncAutoplay();
        });
        this.autoplayObserver.observe(this);

        this.updateAutoplayButton();
      }

      disconnectedCallback() {
        // The theme editor re-renders the section on every setting change. A
        // leaked interval would keep scrolling the removed element.
        clearInterval(this.autoplay);
        this.videos?.forEach((video) => video.pause());
        this.autoplayObserver?.disconnect();
        this.reducedMotion?.removeEventListener(
          "change",
          this.onReducedMotionChange
        );
        document.removeEventListener("visibilitychange", this.syncAutoplay);
      }

      /**
       * @returns {HTMLElement|undefined}
       */
      get currentSlide() {
        if (!this.hasArrows) return this.sliderItems[0];
        return this.sliderItemsToShow?.[this.currentPage - 1];
      }

      /**
       * @returns {HTMLVideoElement|null}
       */
      get currentVideo() {
        return this.currentSlide?.querySelector(VIDEO_SELECTOR) ?? null;
      }

      /**
       * Every condition that must hold for the carousel to rotate itself.
       * One predicate, so hover, focus, visibility and motion preference
       * cannot fight each other.
       * @returns {boolean}
       */
      get shouldAutoplay() {
        if (!this.autoplayEnabled) return false;
        if (this.autoplayIntent === "stopped" || this.rotationHeld) return false;
        if (!(this.sliderItemsToShow?.length > 1)) return false;
        if (!this.isVisible || document.hidden) return false;
        // An explicit press of play is the visitor asking for motion, so it
        // outranks both their OS preference and the courtesy pauses below.
        if (this.autoplayIntent === "playing") return true;
        return !this.reducedMotion.matches && !this.isHovered && !this.isFocused;
      }

      /**
       * Whether the current slide's video should be playing. Hover and focus
       * do not pause it: they pause rotation so a slide can be looked at, and
       * a video is something to look at.
       * @returns {boolean}
       */
      get shouldPlayVideo() {
        if (!this.isVisible || document.hidden) return false;
        return this.autoplayIsIntended;
      }

      /**
       * What the visitor has asked for, ignoring courtesy pauses: hover, focus
       * and scrolling out of view stop the motion without changing this.
       * @returns {boolean}
       */
      get autoplayIsIntended() {
        if (this.autoplayIntent === "stopped") return false;
        if (this.autoplayIntent === "playing") return true;
        return !this.reducedMotion.matches;
      }

      /**
       * Whether the control should read as playing. After an arrow press,
       * rotation is over but videos still play, so the control must still
       * offer to pause them.
       * @returns {boolean}
       */
      get motionIsIntended() {
        if (!this.autoplayIsIntended) return false;
        return this.videos.length > 0 || !this.rotationHeld;
      }

      /**
       * Starts or stops the rotation and the videos to match the predicates
       * above. The single entry point -- every listener flips a flag, then
       * calls this.
       * @returns {void}
       */
      syncAutoplay() {
        clearInterval(this.autoplay);
        this.autoplay = null;

        this.syncVideos();

        // Video slides get the same time on screen as image slides, so a long
        // gameplay clip does not hold up the rotation.
        if (this.shouldAutoplay) {
          this.autoplay = setInterval(() => this.advance(), this.autoplaySpeed);
        }

        // Slide changes are announced only while the carousel is still, so an
        // autoplaying carousel does not talk over the rest of the page.
        this.slider.setAttribute(
          "aria-live",
          this.shouldAutoplay ? "off" : "polite"
        );
      }

      /**
       * Plays the current slide's video and pauses the rest. A video that
       * leaves the screen is rewound, so it plays in full when it comes back
       * round.
       * @returns {void}
       */
      syncVideos() {
        const currentVideo = this.currentVideo;
        const shouldPlay = this.shouldPlayVideo;

        this.videos.forEach((video) => {
          if (video === currentVideo && shouldPlay) {
            // play() rejects when a pause interrupts it; nothing to recover.
            video.play().catch(() => {});
            return;
          }

          video.pause();
          if (video !== currentVideo && video.currentTime > 0) {
            video.currentTime = 0;
          }
        });
      }

      /**
       * @returns {void}
       */
      onSlideChanged() {
        this.setSlideVisibility();
        if (this.motionEnabled) this.syncAutoplay();
      }

      /**
       * @returns {void}
       */
      onAutoplayToggle() {
        if (this.motionIsIntended) {
          this.autoplayIntent = "stopped";
        } else {
          this.autoplayIntent = "playing";
          this.rotationHeld = false;
        }
        this.updateAutoplayButton();
        this.syncAutoplay();
      }

      /**
       * @returns {void}
       */
      onReducedMotionChange() {
        this.updateAutoplayButton();
        this.syncAutoplay();
      }

      /**
       * @returns {void}
       */
      updateAutoplayButton() {
        if (!this.autoplayButton) return;

        const isPlaying = this.motionIsIntended;

        this.autoplayButton.classList.toggle(PAUSED_CLASS, !isPlaying);
        this.autoplayButton.setAttribute(
          "aria-label",
          isPlaying
            ? window.accessibilityStrings.pauseSlideshow
            : window.accessibilityStrings.playSlideshow
        );
      }

      /**
       * Moves to the next slide, returning to the first one past the end.
       * @returns {void}
       */
      advance() {
        this.setSlidePosition(this.atEnd ? 0 : this.nextSlidePosition);
      }

      /**
       * Hides the slides either side of the current one, so the live region
       * announces the slide on screen rather than all of them.
       * @returns {void}
       */
      setSlideVisibility() {
        this.sliderItemsToShow?.forEach((slide, index) => {
          slide.setAttribute("aria-hidden", index !== this.currentPage - 1);
        });
      }

      /**
       * The parent's end-of-slider check is skipped by `enableSliderLooping`,
       * and it compares integer-rounded `offsetLeft` and `clientWidth` values
       * against a fractional `scrollLeft` -- so a sub-pixel slide width leaves
       * the last slide reading as "not visible". Slide widths land on whole
       * pixels at full bleed on mobile, but are fractional on desktop where the
       * media column is 50% of the row. Measuring the scroll box instead is
       * immune to per-slide rounding.
       * @returns {number}
       */
      get maxScrollLeft() {
        return this.slider.scrollWidth - this.slider.clientWidth;
      }

      get atStart() {
        return this.slider.scrollLeft <= SCROLL_TOLERANCE;
      }

      get atEnd() {
        return this.slider.scrollLeft >= this.maxScrollLeft - SCROLL_TOLERANCE;
      }

      get nextSlidePosition() {
        return this.slider.scrollLeft + this.sliderItemOffset;
      }

      get previousSlidePosition() {
        return this.slider.scrollLeft - this.sliderItemOffset;
      }

      /**
       * Replaces the parent handler rather than extending it, so the slider is
       * not scrolled twice. Both arrows wrap around.
       * @param {Event} event - The arrow click event
       * @returns {void}
       */
      onButtonClick(event) {
        event.preventDefault();

        // The visitor is driving now, so stop rotating under them.
        if (this.motionEnabled) {
          this.rotationHeld = true;
          this.updateAutoplayButton();
          this.syncAutoplay();
        }

        if (event.currentTarget.name === "next") {
          this.setSlidePosition(this.atEnd ? 0 : this.nextSlidePosition);
        } else {
          this.setSlidePosition(
            this.atStart ? this.maxScrollLeft : this.previousSlidePosition
          );
        }
      }
    }
  );
}
